import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Sparkles, Check, ArrowLeft, CreditCard, Bitcoin, Zap, Crown, Building2, Gem } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLAN_ICONS = {
  free: Zap,
  pro: Crown,
  business: Building2,
  enterprise: Gem
};

const PLAN_ACCENTS = {
  free: 'text-text-muted border-text-muted/20',
  pro: 'text-champagne border-champagne/30',
  business: 'text-aura-purple border-aura-purple/30',
  enterprise: 'text-aura-gold border-aura-gold/30'
};

function formatStorage(mb) {
  if (mb === -1) return 'Unlimited storage';
  if (mb >= 1000) return `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)}GB storage`;
  return `${mb}MB storage`;
}

function derivedFeatures(plan) {
  const out = [];
  if (plan.max_auras !== undefined && plan.max_auras !== null) {
    out.push(`Up to ${plan.max_auras === -1 ? 'unlimited' : plan.max_auras} ${plan.max_auras === 1 ? 'event' : 'events'}`);
  }
  if (plan.max_media_per_aura !== undefined && plan.max_media_per_aura !== null) {
    out.push(plan.max_media_per_aura === -1 ? 'Unlimited media per event' : `${plan.max_media_per_aura} photos/videos per event`);
  }
  if (plan.max_storage_mb !== undefined && plan.max_storage_mb !== null) {
    out.push(formatStorage(plan.max_storage_mb));
  }
  // Append admin-curated marketing lines, but skip ones that obviously restate the numeric caps to avoid duplicates.
  const cap = /\b(\d+\s*(GB|MB)\s*storage|up to \d+ events?|\d+ photos?|unlimited (events|storage|media))\b/i;
  for (const f of (plan.features || [])) {
    if (!cap.test(f)) out.push(f);
  }
  return out;
}

export default function PricingPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [notifyPlan, setNotifyPlan] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState('');

  const submitWaitlist = async () => {
    if (!notifyPlan) return;
    const email = notifyEmail.trim();
    if (!email || !email.includes('@')) { setNotifyStatus('Please enter a valid email.'); return; }
    setNotifyStatus('sending');
    try {
      await axios.post(`${API_URL}/api/plans/${notifyPlan}/waitlist`, { email });
      setNotifyStatus('done');
    } catch (e) {
      setNotifyStatus('error');
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/plans`);
      setPlans(data.plans);
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (planKey, method) => {
    if (!user) {
      navigate('/register');
      return;
    }
    if (planKey === 'free') return;

    setCheckoutLoading(`${planKey}-${method}`);
    try {
      const endpoint = method === 'bitcoin' ? '/api/checkout/bitcoin' : 
                       method === 'paypal' ? '/api/checkout/paypal' :
                       '/api/checkout/stripe';
      
      const { data } = await axios.post(`${API_URL}${endpoint}`, {
        plan: planKey,
        billing_cycle: billingCycle,
        payment_method: method,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Checkout failed. Please try again.';
      alert(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const canonical = ['free', 'pro', 'business', 'enterprise', 'agency'];
  const planOrder = [
    ...canonical.filter(k => plans[k]),
    ...Object.keys(plans).filter(k => !canonical.includes(k))
      .sort((a, b) => (plans[a]?.price_monthly ?? 0) - (plans[b]?.price_monthly ?? 0)),
  ];

  return (
    <div className="min-h-screen bg-midnight overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-aura-purple/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-aura-gold/10 rounded-full blur-3xl" />
      </div>

      {/* Nav */}
      <nav className="relative z-50 border-b border-champagne/10 bg-midnight/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="pricing-nav-home">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-serif font-medium text-text-light tracking-tight truncate">Memouraa</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard" className="text-text-muted hover:text-champagne transition-colors" data-testid="pricing-nav-dashboard">
                <ArrowLeft className="w-4 h-4 inline mr-1" />{t('common.dashboard')}
              </Link>
            ) : (
              <Link to="/login" className="text-text-muted hover:text-champagne transition-colors" data-testid="pricing-nav-login">
                {t('common.signIn')}
              </Link>
            )}
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-5xl font-serif font-bold text-text-light mb-4" data-testid="pricing-title">
            {t('pricing.title')}
          </h1>
          <p className="text-text-muted text-lg max-w-2xl mx-auto mb-6">
            {t('pricing.subtitle')}
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-champagne/10 border border-champagne/30 text-champagne text-sm">
            <span className="w-2 h-2 rounded-full bg-champagne animate-pulse" />
            One $19/month plan = 16 weddings/year vs. ~$5,000+ at one-shot tools
          </div>
        </motion.div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12" data-testid="billing-toggle">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-champagne' : 'text-text-muted'}`}>{t('pricing.monthly')}</span>
          <button
            onClick={() => setBillingCycle(b => b === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-champagne' : 'bg-midnight-light'}`}
            data-testid="billing-cycle-switch"
          >
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-champagne' : 'text-text-muted'}`}>
            {t('pricing.yearly')} <span className="text-xs text-success ml-1">{t('pricing.saveAnnual')}</span>
          </span>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="text-center text-text-muted py-20">Loading plans...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {planOrder.map((key, idx) => {
              const plan = plans[key];
              if (!plan) return null;
              const Icon = PLAN_ICONS[key] || Zap;
              const accent = PLAN_ACCENTS[key];
              const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
              const isCurrentPlan = user?.plan === key;
              const isPopular = key === 'pro';

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`relative rounded-2xl border ${isPopular ? 'border-champagne/40 bg-champagne/5' : 'border-white/10 bg-white/[0.02]'} p-6 flex flex-col`}
                  data-testid={`plan-card-${key}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-champagne text-midnight text-xs font-bold px-4 py-1 rounded-full">
                      {t('pricing.mostPopular')}
                    </div>
                  )}
                  {plan.coming_soon && (
                    <div className="absolute -top-3 right-4 bg-aura-gold text-midnight text-xs font-bold px-3 py-1 rounded-full tracking-wide">
                      {t('pricing.comingSoon')}
                    </div>
                  )}
                  
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 border ${accent}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  
                  <h3 className="text-xl font-serif font-bold text-text-light mb-1">{plan.name}</h3>
                  
                  <div className="mb-4">
                    {price === 0 ? (
                      <span className="text-3xl font-bold text-text-light">Free</span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-text-light">${price}</span>
                        <span className="text-text-muted text-sm">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                      </>
                    )}
                  </div>
                  
                  <ul className="space-y-2 mb-6 flex-1">
                    {derivedFeatures(plan).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-text-muted">
                        <Check className="w-4 h-4 text-champagne mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {isCurrentPlan ? (
                    <div className="text-center py-3 rounded-xl border border-champagne/30 text-champagne text-sm font-medium" data-testid={`current-plan-${key}`}>
                      Current Plan
                    </div>
                  ) : plan.coming_soon ? (
                    <button
                      onClick={() => { setNotifyPlan(key); setNotifyEmail(''); setNotifyStatus(''); }}
                      className="w-full py-3 rounded-xl border border-aura-gold/40 bg-aura-gold/5 text-aura-gold text-sm font-medium hover:bg-aura-gold/10 transition-colors"
                      data-testid={`notify-me-${key}`}
                    >
                      Notify me when it launches
                    </button>
                  ) : key === 'free' ? (
                    <div className="text-center py-3 rounded-xl border border-white/10 text-text-muted text-sm">
                      Free Forever
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleCheckout(key, 'stripe')}
                        disabled={checkoutLoading === `${key}-stripe`}
                        className="w-full py-3 rounded-xl bg-champagne text-midnight font-semibold text-sm hover:bg-champagne-light transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`checkout-stripe-${key}`}
                      >
                        <CreditCard className="w-4 h-4" />
                        {checkoutLoading === `${key}-stripe` ? 'Processing...' : 'Pay with Card'}
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCheckout(key, 'paypal')}
                          disabled={checkoutLoading === `${key}-paypal`}
                          className="flex-1 py-2 rounded-xl border border-blue-400/30 text-blue-400 text-xs font-medium hover:bg-blue-400/10 transition-colors disabled:opacity-50"
                          data-testid={`checkout-paypal-${key}`}
                        >
                          {checkoutLoading === `${key}-paypal` ? '...' : 'PayPal'}
                        </button>
                        <button
                          onClick={() => handleCheckout(key, 'bitcoin')}
                          disabled={checkoutLoading === `${key}-bitcoin`}
                          className="flex-1 py-2 rounded-xl border border-orange-400/30 text-orange-400 text-xs font-medium hover:bg-orange-400/10 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                          data-testid={`checkout-bitcoin-${key}`}
                        >
                          <Bitcoin className="w-3 h-3" />
                          {checkoutLoading === `${key}-bitcoin` ? '...' : 'Bitcoin'}
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Competitor comparison */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif text-center text-text-light mb-2">
            How we compare
          </h2>
          <p className="text-center text-text-muted mb-8">Per-event tools charge once. We charge per month. Run unlimited events for the same price.</p>
          <div className="aura-card rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-champagne/5 border-b border-champagne/20">
                <tr>
                  <th className="text-left px-4 py-3 text-text-muted font-medium">Feature</th>
                  <th className="text-center px-4 py-3 text-champagne font-medium">Memouraa Pro</th>
                  <th className="text-center px-4 py-3 text-text-muted font-medium">Single-event apps</th>
                  <th className="text-center px-4 py-3 text-text-muted font-medium">Per-event SaaS</th>
                  <th className="text-center px-4 py-3 text-text-muted font-medium">Enterprise event apps</th>
                </tr>
              </thead>
              <tbody className="text-text-light">
                {[
                  { f: 'Pricing model',         m: '$19/mo unlimited',  p: 'Per-event fee',  w: 'Per-event fee', e: 'Annual contract' },
                  { f: 'Live AuraCast wall',     m: '✓ WebSocket',       p: 'rare',           w: 'sometimes',     e: '✓' },
                  { f: 'Saudade Timer (fade)',   m: '✓ unique',          p: '—',              w: '—',             e: '—' },
                  { f: 'Voice toasts',           m: '✓',                  p: '—',              w: '—',             e: '—' },
                  { f: 'Mood tagging + analytics', m: '✓',              p: '—',              w: '—',             e: 'basic' },
                  { f: 'Find Me (face search)',  m: '✓ Business+',       p: 'sometimes',      w: '—',             e: '✓' },
                  { f: 'Auto highlight reel',    m: '✓ Pro+',            p: '—',              w: '—',             e: '—' },
                  { f: 'Memory Report PDF',      m: '✓ Pro+',            p: '—',              w: '—',             e: '—' },
                  { f: 'White-label branding',   m: '✓ Enterprise',      p: '—',              w: '—',             e: 'addon' },
                  { f: 'API access',             m: '✓ Enterprise',      p: '—',              w: '—',             e: '—' },
                  { f: 'Co-host collaboration',  m: '✓ Business+',       p: '—',              w: '—',             e: '—' },
                  { f: 'Wedding website + RSVP', m: '✓',                  p: '—',              w: '—',             e: '—' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-midnight/30' : ''}>
                    <td className="px-4 py-3 text-text-muted">{row.f}</td>
                    <td className="text-center px-4 py-3 text-champagne font-medium">{row.m}</td>
                    <td className="text-center px-4 py-3 text-text-muted">{row.p}</td>
                    <td className="text-center px-4 py-3 text-text-muted">{row.w}</td>
                    <td className="text-center px-4 py-3 text-text-muted">{row.e}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Existing customer billing management */}
        {user && user.plan && user.plan !== 'free' && (
          <div className="text-center mt-12">
            <button
              onClick={async () => {
                try {
                  const { data } = await axios.get(`${API_URL}/api/billing/portal-link`, { withCredentials: true });
                  window.location.href = data.url;
                } catch (err) {
                  alert(err.response?.data?.detail || 'Could not open billing portal');
                }
              }}
              className="text-champagne hover:underline text-sm"
            >
              Manage your billing & invoices →
            </button>
          </div>
        )}

        {/* FAQ note */}
        <div className="text-center mt-16 text-text-muted text-sm">
          <p>All plans include SSL encryption, 99.9% uptime guarantee, and 24/7 monitoring.</p>
          <p className="mt-2">Need a custom plan? <span className="text-champagne">Contact us</span></p>
        </div>
      </div>

      {notifyPlan && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setNotifyPlan(null)} data-testid="notify-modal">
          <div className="bg-midnight-light border border-champagne/30 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-champagne mb-2">Notify me</h3>
            <p className="text-text-muted text-sm mb-4">We'll email you when the <strong className="text-text-light">{plans[notifyPlan]?.name || notifyPlan}</strong> plan launches.</p>
            {notifyStatus === 'done' ? (
              <div className="text-success text-sm mb-4">Thanks — you're on the list.</div>
            ) : (
              <>
                <input
                  type="email"
                  autoFocus
                  value={notifyEmail}
                  onChange={e => setNotifyEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 rounded-lg bg-midnight border border-white/10 text-text-light text-sm mb-3 focus:outline-none focus:border-champagne/40"
                  data-testid="notify-email-input"
                />
                {notifyStatus && notifyStatus !== 'sending' && notifyStatus !== 'done' && (
                  <div className="text-error text-xs mb-2">{notifyStatus === 'error' ? 'Could not save. Try again.' : notifyStatus}</div>
                )}
              </>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNotifyPlan(null)} className="px-4 py-2 text-sm text-text-muted hover:text-text-light">
                {notifyStatus === 'done' ? 'Close' : 'Cancel'}
              </button>
              {notifyStatus !== 'done' && (
                <button
                  onClick={submitWaitlist}
                  disabled={notifyStatus === 'sending'}
                  className="px-4 py-2 text-sm rounded-lg bg-champagne text-midnight font-medium hover:bg-champagne-light disabled:opacity-50"
                  data-testid="notify-submit"
                >
                  {notifyStatus === 'sending' ? 'Sending…' : 'Submit'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <FooterExpanded />
    </div>
  );
}
